import { useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GeoJSONStoreFeatures,
  HexColor,
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { SERVICE_AREA_COLORS, ServiceAreaType } from '../constants';

const SERVICE_AREA_DRAW_BATCH_SIZE = 8;
const SERVICE_AREA_MAP_DOM_ID = 'service-area-map';
const CIRCLE_POLYGON_STEPS = 128;
const COORDINATE_PRECISION = 9;
const EARTH_RADIUS_KM = 6371;
/** Custom property: true circle center as [lng, lat] (GeoJSON order). */
const CIRCLE_CENTER_PROPERTY = 'center';
/**
 * Drag vs center-fixed resize:
 * - Resize keeps the true center (ring average only wobbles from vertex rounding).
 * - Drag translates the ring, so average center moves by roughly the drag delta.
 * Treat as drag when center shift exceeds both an absolute floor (5m) and 2% of radius.
 */
const CIRCLE_DRAG_CENTER_SHIFT_MIN_KM = 0.005;
const CIRCLE_DRAG_CENTER_SHIFT_RATIO = 0.02;
/**
 * `resizable: 'center'` can stretch a circle into an oval (independent x/y scale).
 * Treat as non-circular when radius span exceeds this fraction of the mean radius.
 */
const CIRCLE_OVAL_RADIUS_VARIATION_RATIO = 0.05;

const scheduleServiceAreaDrawBatch = (callback: () => void) => {
  if (typeof requestIdleCallback !== 'undefined') {
    const handle = requestIdleCallback(callback, { timeout: 32 });
    return () => cancelIdleCallback(handle);
  }

  const handle = requestAnimationFrame(callback);
  return () => cancelAnimationFrame(handle);
};

const toHexColor = (color: string): HexColor => {
  return (color.startsWith('#') ? color : `#${color}`) as HexColor;
};

const toRad = (degrees: number) => (degrees * Math.PI) / 180;
const toDeg = (radians: number) => (radians * 180) / Math.PI;

// Terra Draw rejects coordinates with more than coordinatePrecision decimal places
const limitPrecision = (value: number, precision = COORDINATE_PRECISION) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const limitLngLat = ([lng, lat]: [number, number]): [number, number] => [limitPrecision(lng), limitPrecision(lat)];

const destinationLngLat = (lng: number, lat: number, distanceKm: number, bearingDeg: number): [number, number] => {
  const angularDistance = distanceKm / EARTH_RADIUS_KM;
  const bearing = toRad(bearingDeg);
  const lat1 = toRad(lat);
  const lng1 = toRad(lng);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) + Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );

  return limitLngLat([toDeg(lng2), toDeg(lat2)]);
};

const haversineDistanceKm = ([lng1, lat1]: [number, number], [lng2, lat2]: [number, number]): number => {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
};

const createCircleRing = (lng: number, lat: number, radiusKm: number): [number, number][] => {
  const ring: [number, number][] = [];
  for (let index = 0; index < CIRCLE_POLYGON_STEPS; index += 1) {
    ring.push(destinationLngLat(lng, lat, radiusKm, (-360 * index) / CIRCLE_POLYGON_STEPS));
  }
  ring.push(ring[0]);
  return ring;
};

const averageRingCenter = (openRing: [number, number][]): [number, number] => {
  const lng = openRing.reduce((sum, point) => sum + point[0], 0) / openRing.length;
  const lat = openRing.reduce((sum, point) => sum + point[1], 0) / openRing.length;
  return [lng, lat];
};

const getStoredCircleCenter = (feature: GeoJSONStoreFeatures): [number, number] | null => {
  const center = feature.properties?.[CIRCLE_CENTER_PROPERTY];
  if (!Array.isArray(center) || center.length < 2) {
    return null;
  }

  const lng = Number(center[0]);
  const lat = Number(center[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [lng, lat];
};

/** Radius from geometry — select-mode resize updates the ring but not radiusKilometers. */
const getCircleRadiusKmFromGeometry = (center: [number, number], openRing: [number, number][]): number => {
  if (!openRing.length) return 0;
  const total = openRing.reduce((sum, point) => sum + haversineDistanceKm(center, point), 0);
  return total / openRing.length;
};

/** True when ring distances from center are nearly equal (still a circle, not an oval). */
const isApproximatelyCircular = (center: [number, number], openRing: [number, number][]): boolean => {
  if (openRing.length < 3) return false;

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let total = 0;

  for (const point of openRing) {
    const distanceKm = haversineDistanceKm(center, point);
    min = Math.min(min, distanceKm);
    max = Math.max(max, distanceKm);
    total += distanceKm;
  }

  const averageKm = total / openRing.length;
  if (averageKm <= 0) return false;

  return (max - min) / averageKm <= CIRCLE_OVAL_RADIUS_VARIATION_RATIO;
};

const ringToPolygonVertices = (openRing: [number, number][]): [number, number][] =>
  openRing.map(([lng, lat]) => [lat, lng] as [number, number]);

const OUTLINE_WIDTH_ZOOM_OUT = 4;
const OUTLINE_WIDTH_DEFAULT = 1.5;
const OUTLINE_THICKEN_BELOW_ZOOM = 5;

const getOutlineWidthForZoom = (zoom: number | undefined) => {
  if (zoom === undefined || zoom < OUTLINE_THICKEN_BELOW_ZOOM) {
    return OUTLINE_WIDTH_ZOOM_OUT;
  }
  return OUTLINE_WIDTH_DEFAULT;
};

const getPolygonFillStyles = (getColor: () => string, getZoom: () => number | undefined) => ({
  fillColor: () => toHexColor(getColor()),
  fillOpacity: 0.3,
  outlineColor: () => toHexColor(getColor()),
  // Thicker outline at low zoom blends polygon-circle facet artifacts
  outlineWidth: () => getOutlineWidthForZoom(getZoom())
});

const getSelectStyles = (getColor: () => string, getZoom: () => number | undefined) => ({
  selectedPolygonColor: () => toHexColor(getColor()),
  selectedPolygonFillOpacity: 0.3,
  selectedPolygonOutlineColor: () => toHexColor(getColor()),
  selectedPolygonOutlineWidth: () => getOutlineWidthForZoom(getZoom())
});

export type DrawingMode = 'static' | 'select' | 'polygon' | 'circle';

export type ServiceAreaShapePayload = {
  type: ServiceAreaType;
  data: [number, number][] | { centroid: [number, number]; range: number };
};

export interface DrawingController {
  setMode: (mode: DrawingMode) => void;
  getMode: () => string;
  clear: () => void;
}

interface InitialShapeData {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
}

const getInitialShapeSignature = (shape?: InitialShapeData): string => {
  if (!shape) return '';
  if (shape.type === ServiceAreaType.CIRCLE) {
    return `circle:${shape.centroid?.[0] ?? ''},${shape.centroid?.[1] ?? ''},${shape.range ?? ''}`;
  }
  return `polygon:${JSON.stringify(shape.vertices ?? [])}`;
};

export interface ExistingShape {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
  color: string;
  strokeWidth?: number;
}

export interface DrawingManagerResult {
  controller: DrawingController | null;
  activeMode: DrawingMode;
  hasCurrentFeature: boolean;
  clearCurrentFeature: () => void;
}

const isEditableDrawFeature = (feature: GeoJSONStoreFeatures): boolean => {
  const mode = feature.properties?.mode;
  if (mode !== 'polygon' && mode !== 'circle') {
    return false;
  }

  // Ignore in-progress drawings until Terra Draw emits finish
  if (feature.properties?.currentlyDrawing) {
    return false;
  }

  return true;
};

const featureToShapePayload = (feature: GeoJSONStoreFeatures): ServiceAreaShapePayload | null => {
  if (!isEditableDrawFeature(feature) || feature.geometry.type !== 'Polygon') {
    return null;
  }

  const ring = feature.geometry.coordinates[0] ?? [];
  if (ring.length < 4) {
    return null;
  }

  if (feature.properties.mode === 'circle') {
    const openRing = ring.slice(0, -1) as [number, number][];
    const storedCenter = getStoredCircleCenter(feature);
    const [centerLng, centerLat] = storedCenter ?? averageRingCenter(openRing);

    // Stretched circle (oval) must persist as polygon vertices, not centroid+range
    if (!isApproximatelyCircular([centerLng, centerLat], openRing)) {
      return {
        type: ServiceAreaType.POLYGON,
        data: ringToPolygonVertices(openRing)
      };
    }

    const radiusKilometers = getCircleRadiusKmFromGeometry([centerLng, centerLat], openRing);

    if (!Number.isFinite(radiusKilometers) || radiusKilometers <= 0) {
      return null;
    }

    return {
      type: ServiceAreaType.CIRCLE,
      data: {
        centroid: [centerLat, centerLng],
        range: radiusKilometers * 1000
      }
    };
  }

  // Stored service-area vertices use [lat, lng]
  const vertices = ringToPolygonVertices(ring.slice(0, -1) as [number, number][]);
  return {
    type: ServiceAreaType.POLYGON,
    data: vertices
  };
};

const initialShapeToFeature = (shape: InitialShapeData): GeoJSONStoreFeatures | null => {
  if (shape.type === ServiceAreaType.POLYGON && shape.vertices?.length) {
    const ring = shape.vertices.map(([lat, lng]) => limitLngLat([lng, lat]));
    if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
      ring.push(ring[0]);
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ring]
      },
      properties: {
        mode: 'polygon'
      }
    };
  }

  if (shape.type === ServiceAreaType.CIRCLE && shape.centroid && shape.range) {
    const [lat, lng] = shape.centroid;
    const radiusKilometers = shape.range / 1000;
    if (radiusKilometers <= 0) {
      return null;
    }

    const center = limitLngLat([lng, lat]);

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [createCircleRing(center[0], center[1], radiusKilometers)]
      },
      properties: {
        mode: 'circle',
        radiusKilometers: limitPrecision(radiusKilometers),
        [CIRCLE_CENTER_PROPERTY]: center
      }
    };
  }

  return null;
};

export function useDrawingManager(
  color = SERVICE_AREA_COLORS[0],
  onShapeComplete?: (shape: ServiceAreaShapePayload) => void,
  initialShapeData?: InitialShapeData,
  onShapeClear?: () => void,
  existingShapes?: ExistingShape[],
  showExistingShapes = true
): DrawingManagerResult {
  const map = useMap();
  const [controller, setController] = useState<DrawingController | null>(null);
  const [activeMode, setActiveMode] = useState<DrawingMode>('static');
  const [hasCurrentFeature, setHasCurrentFeature] = useState(false);

  const drawRef = useRef<TerraDraw | null>(null);
  const currentColorRef = useRef(color);
  const onShapeCompleteRef = useRef(onShapeComplete);
  const onShapeClearRef = useRef(onShapeClear);
  const initialShapeDataRef = useRef(initialShapeData);
  const initialShapeLoadedRef = useRef(false);
  const loadedShapeSignatureRef = useRef('');
  const existingOverlaysRef = useRef<(google.maps.Polygon | google.maps.Circle)[]>([]);
  const isApplyingProgrammaticChangeRef = useRef(false);
  const showExistingShapesRef = useRef(showExistingShapes);

  onShapeCompleteRef.current = onShapeComplete;
  onShapeClearRef.current = onShapeClear;
  currentColorRef.current = color;
  initialShapeDataRef.current = initialShapeData;
  showExistingShapesRef.current = showExistingShapes;

  const getColor = useCallback(() => currentColorRef.current, []);

  const clearExistingOverlays = useCallback(() => {
    existingOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    existingOverlaysRef.current = [];
  }, []);

  const emitFeatureShape = useCallback((featureId: string | number) => {
    const draw = drawRef.current;
    if (!draw) return;

    const feature = draw.getSnapshotFeature(featureId);
    if (!feature) return;

    const payload = featureToShapePayload(feature);
    if (payload && onShapeCompleteRef.current) {
      onShapeCompleteRef.current(payload);
    }
  }, []);

  const syncHasCurrentFeature = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) {
      setHasCurrentFeature(false);
      return;
    }

    const hasFeature = draw.getSnapshot().some(feature => {
      const mode = feature.properties?.mode;
      return mode === 'polygon' || mode === 'circle';
    });
    setHasCurrentFeature(hasFeature);
  }, []);

  const setDrawMode = useCallback((mode: DrawingMode) => {
    const draw = drawRef.current;
    if (!draw) return;
    draw.setMode(mode);
    setActiveMode(mode);
  }, []);

  const clearCurrentFeature = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;

    isApplyingProgrammaticChangeRef.current = true;
    draw.clear();
    isApplyingProgrammaticChangeRef.current = false;
    setHasCurrentFeature(false);
    setDrawMode('static');

    if (onShapeClearRef.current) {
      onShapeClearRef.current();
    }
  }, [setDrawMode]);

  /**
   * Keep circle center/radius properties aligned after select-mode edits.
   * Non-uniform resize (`resizable: 'center'`) can produce an oval — skip circle sync so
   * featureToShapePayload persists vertices as POLYGON (do not change reserved `mode` here).
   */
  const syncCircleMetrics = useCallback((draw: TerraDraw, featureId: string | number) => {
    const feature = draw.getSnapshotFeature(featureId);
    if (!feature || feature.properties?.mode !== 'circle' || feature.geometry.type !== 'Polygon') {
      return;
    }

    const openRing = (feature.geometry.coordinates[0] ?? []).slice(0, -1) as [number, number][];
    if (openRing.length < 3) return;

    const storedCenter = getStoredCircleCenter(feature);
    const averageCenter = limitLngLat(averageRingCenter(openRing));

    // Center-fixed resize keeps the stored center; drag translates the ring.
    let center = averageCenter;
    if (storedCenter) {
      const radiusFromStored = getCircleRadiusKmFromGeometry(storedCenter, openRing);
      const centerShiftKm = haversineDistanceKm(storedCenter, averageCenter);
      const dragShiftThresholdKm = Math.max(
        CIRCLE_DRAG_CENTER_SHIFT_MIN_KM,
        radiusFromStored * CIRCLE_DRAG_CENTER_SHIFT_RATIO
      );
      const isLikelyDrag = centerShiftKm > dragShiftThresholdKm;
      center = isLikelyDrag ? averageCenter : limitLngLat(storedCenter);
    }

    // Stretched oval — leave geometry alone; save path emits POLYGON vertices
    if (!isApproximatelyCircular(center, openRing)) {
      return;
    }

    const radiusKilometers = limitPrecision(getCircleRadiusKmFromGeometry(center, openRing));
    if (radiusKilometers <= 0) return;

    const prevRadius = Number(feature.properties.radiusKilometers);
    const centerChanged = !storedCenter || storedCenter[0] !== center[0] || storedCenter[1] !== center[1];
    const radiusChanged = prevRadius !== radiusKilometers;

    if (!centerChanged && !radiusChanged) return;

    isApplyingProgrammaticChangeRef.current = true;
    draw.updateFeatureProperties(featureId, {
      [CIRCLE_CENTER_PROPERTY]: center,
      radiusKilometers
    });
    isApplyingProgrammaticChangeRef.current = false;
  }, []);

  const loadInitialShape = useCallback(
    (draw: TerraDraw) => {
      const shapeData = initialShapeDataRef.current;
      if (!shapeData || initialShapeLoadedRef.current) return false;

      const feature = initialShapeToFeature(shapeData);
      if (!feature) return false;

      feature.id = draw.getFeatureId();

      isApplyingProgrammaticChangeRef.current = true;
      const results = draw.addFeatures([feature]);
      isApplyingProgrammaticChangeRef.current = false;

      const added = results[0];
      if (!added?.valid) {
        return false;
      }

      const featureId = added.id ?? feature.id;
      if (featureId === undefined) {
        return false;
      }

      initialShapeLoadedRef.current = true;
      syncHasCurrentFeature();
      draw.setMode('select');
      setActiveMode('select');
      draw.selectFeature(featureId);
      emitFeatureShape(featureId);
      return true;
    },
    [emitFeatureShape, syncHasCurrentFeature]
  );

  const createExistingShapeOverlay = useCallback(
    (shape: ExistingShape, mapInstance: google.maps.Map): google.maps.Polygon | google.maps.Circle | null => {
      const shouldShow = showExistingShapesRef.current;
      const overlayOptions = {
        fillColor: shape.color,
        strokeColor: shape.color,
        fillOpacity: 0.1,
        strokeWeight: shape.strokeWidth || 1,
        editable: false,
        draggable: false,
        // Keep existing areas under the editable Terra Draw feature
        zIndex: 1,
        map: shouldShow ? mapInstance : null,
        clickable: false
      };

      if (shape.type === ServiceAreaType.POLYGON && shape.vertices?.length) {
        const paths = shape.vertices.map(([lat, lng]) => new google.maps.LatLng(lat, lng));
        return new google.maps.Polygon({
          ...overlayOptions,
          paths
        });
      }

      if (shape.type === ServiceAreaType.CIRCLE && shape.centroid && shape.range) {
        return new google.maps.Circle({
          ...overlayOptions,
          center: new google.maps.LatLng(shape.centroid[0], shape.centroid[1]),
          radius: shape.range
        });
      }

      return null;
    },
    []
  );

  // Create existing shapes asynchronously in batches (core Polygon/Circle APIs)
  useEffect(() => {
    if (!map || !existingShapes?.length) {
      clearExistingOverlays();
      return;
    }

    let cancelled = false;
    let cancelPendingBatch: (() => void) | null = null;

    clearExistingOverlays();

    const processBatch = (startIndex: number) => {
      if (cancelled) return;

      const endIndex = Math.min(startIndex + SERVICE_AREA_DRAW_BATCH_SIZE, existingShapes.length);

      for (let index = startIndex; index < endIndex; index += 1) {
        const overlay = createExistingShapeOverlay(existingShapes[index], map);
        if (overlay) {
          existingOverlaysRef.current.push(overlay);
        }
      }

      if (endIndex < existingShapes.length) {
        cancelPendingBatch = scheduleServiceAreaDrawBatch(() => processBatch(endIndex));
      }
    };

    processBatch(0);

    return () => {
      cancelled = true;
      cancelPendingBatch?.();
      cancelPendingBatch = null;
      clearExistingOverlays();
    };
  }, [map, existingShapes, clearExistingOverlays, createExistingShapeOverlay]);

  // Keep overlay map attachment in sync with the "Show all service areas" toggle
  useEffect(() => {
    if (!map) return;
    existingOverlaysRef.current.forEach(overlay => {
      overlay.setMap(showExistingShapes ? map : null);
    });
  }, [map, showExistingShapes, existingShapes]);

  const loadInitialShapeRef = useRef(loadInitialShape);
  const clearCurrentFeatureRef = useRef(clearCurrentFeature);
  const emitFeatureShapeRef = useRef(emitFeatureShape);
  const syncCircleMetricsRef = useRef(syncCircleMetrics);
  const syncHasCurrentFeatureRef = useRef(syncHasCurrentFeature);
  const getColorRef = useRef(getColor);

  loadInitialShapeRef.current = loadInitialShape;
  clearCurrentFeatureRef.current = clearCurrentFeature;
  emitFeatureShapeRef.current = emitFeatureShape;
  syncCircleMetricsRef.current = syncCircleMetrics;
  syncHasCurrentFeatureRef.current = syncHasCurrentFeature;
  getColorRef.current = getColor;

  // Initialize Terra Draw once the map projection is ready
  useEffect(() => {
    if (!map || drawRef.current) return;

    const mapDiv = map.getDiv();
    if (!mapDiv.id) {
      mapDiv.id = SERVICE_AREA_MAP_DOM_ID;
    }

    let cancelled = false;
    let projectionListener: google.maps.MapsEventListener | null = null;
    let zoomListener: google.maps.MapsEventListener | null = null;

    const forceFeatureRestyle = (draw: TerraDraw) => {
      isApplyingProgrammaticChangeRef.current = true;
      draw
        .getSnapshot()
        .filter(feature => {
          const mode = feature.properties?.mode;
          return mode === 'polygon' || mode === 'circle';
        })
        .forEach(feature => {
          if (feature.id === undefined) return;
          draw.updateFeatureGeometry(feature.id, feature.geometry);
        });
      isApplyingProgrammaticChangeRef.current = false;
    };

    const initTerraDraw = () => {
      if (cancelled || drawRef.current || !map.getProjection()) return;

      const getZoom = () => map.getZoom();
      const polygonStyles = getPolygonFillStyles(() => getColorRef.current(), getZoom);
      const selectStyles = getSelectStyles(() => getColorRef.current(), getZoom);

      const draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: google.maps,
          coordinatePrecision: COORDINATE_PRECISION,
          isolatedData: true
        }),
        modes: [
          new TerraDrawRenderMode({
            modeName: 'static',
            styles: {}
          }),
          new TerraDrawSelectMode({
            styles: selectStyles,
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true
                  }
                }
              },
              circle: {
                feature: {
                  draggable: true,
                  coordinates: {
                    resizable: 'center'
                  }
                }
              }
            }
          }),
          new TerraDrawPolygonMode({
            styles: polygonStyles
          }),
          new TerraDrawCircleMode({
            styles: polygonStyles,
            segments: CIRCLE_POLYGON_STEPS
          })
        ]
      });

      draw.start();

      // Google Maps does not re-run Terra Draw's StyleFunction on zoom alone —
      // nudge features so zoom-based outlineWidth is reapplied.
      zoomListener = map.addListener('zoom_changed', () => {
        if (cancelled || !drawRef.current) return;
        forceFeatureRestyle(draw);
      });

      const handleReady = () => {
        if (cancelled) return;

        const drawingController: DrawingController = {
          setMode: (mode: DrawingMode) => {
            draw.setMode(mode);
            setActiveMode(mode);
          },
          getMode: () => draw.getMode(),
          clear: () => clearCurrentFeatureRef.current()
        };

        setController(drawingController);
        draw.setMode('static');
        setActiveMode('static');
        loadInitialShapeRef.current(draw);
      };

      const handleFinish = (featureId: string | number) => {
        if (isApplyingProgrammaticChangeRef.current) return;

        const snapshot = draw.getSnapshot().filter(isEditableDrawFeature);
        const idsToRemove = snapshot
          .filter(feature => feature.id !== featureId)
          .map(feature => feature.id)
          .filter((id): id is string | number => id !== undefined);

        if (idsToRemove.length) {
          isApplyingProgrammaticChangeRef.current = true;
          draw.removeFeatures(idsToRemove);
          isApplyingProgrammaticChangeRef.current = false;
        }

        // Newly drawn circles get a stable center property from geometry.
        syncCircleMetricsRef.current(draw, featureId);
        setHasCurrentFeature(true);
        emitFeatureShapeRef.current(featureId);
        draw.setMode('select');
        setActiveMode('select');
        draw.selectFeature(featureId);
      };

      const handleChange = (ids: (string | number)[]) => {
        if (isApplyingProgrammaticChangeRef.current) return;

        syncHasCurrentFeatureRef.current();

        ids.forEach(id => {
          const feature = draw.getSnapshotFeature(id);
          if (!feature || !isEditableDrawFeature(feature)) return;

          if (feature.properties?.mode === 'circle') {
            syncCircleMetricsRef.current(draw, id);
          }
          emitFeatureShapeRef.current(id);
        });
      };

      draw.on('ready', handleReady);
      draw.on('finish', handleFinish);
      draw.on('change', handleChange);

      drawRef.current = draw;
    };

    projectionListener = map.addListener('projection_changed', initTerraDraw);
    initTerraDraw();

    return () => {
      cancelled = true;
      if (projectionListener) {
        google.maps.event.removeListener(projectionListener);
      }
      if (zoomListener) {
        google.maps.event.removeListener(zoomListener);
      }
      if (drawRef.current) {
        drawRef.current.stop();
        drawRef.current = null;
      }
      setController(null);
      setActiveMode('static');
      setHasCurrentFeature(false);
      // Do not clear existing shape overlays here — they have their own lifecycle.
      // Clearing them on Terra Draw teardown left the map empty until a full remount.
    };
  }, [map]);

  // Apply color updates — style fns read currentColorRef; force a documented geometry
  // update so the Google Maps adapter re-evaluates its StyleFunction (pure "styling"
  // events leave already-rendered features unchanged in this adapter).
  useEffect(() => {
    currentColorRef.current = color;
    const draw = drawRef.current;
    if (!draw || !map) return;

    const getZoom = () => map.getZoom();
    const polygonStyles = getPolygonFillStyles(getColor, getZoom);
    const selectStyles = getSelectStyles(getColor, getZoom);

    draw.updateModeOptions('polygon', { styles: polygonStyles });
    draw.updateModeOptions('circle', { styles: polygonStyles });
    draw.updateModeOptions('select', { styles: selectStyles });

    isApplyingProgrammaticChangeRef.current = true;
    draw
      .getSnapshot()
      .filter(feature => {
        const mode = feature.properties?.mode;
        return mode === 'polygon' || mode === 'circle';
      })
      .forEach(feature => {
        if (feature.id === undefined) return;
        draw.updateFeatureGeometry(feature.id, feature.geometry);
      });
    isApplyingProgrammaticChangeRef.current = false;
  }, [color, getColor, map]);

  // Load / reload initial shape when Terra Draw is ready or shape content changes.
  // Use a content signature (not object identity) so parent re-renders don't retrigger.
  const initialShapeSignature = getInitialShapeSignature(initialShapeData);

  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !controller) return;

    if (loadedShapeSignatureRef.current === initialShapeSignature) {
      // Content unchanged; still try once if we never successfully loaded
      if (!initialShapeLoadedRef.current && initialShapeSignature) {
        loadInitialShape(draw);
      }
      return;
    }

    // Shape identity changed — clear prior editable geometry and allow reload
    loadedShapeSignatureRef.current = initialShapeSignature;
    initialShapeLoadedRef.current = false;

    if (!initialShapeSignature) {
      isApplyingProgrammaticChangeRef.current = true;
      draw.clear();
      isApplyingProgrammaticChangeRef.current = false;
      setHasCurrentFeature(false);
      return;
    }

    isApplyingProgrammaticChangeRef.current = true;
    draw.clear();
    isApplyingProgrammaticChangeRef.current = false;
    loadInitialShape(draw);
  }, [controller, initialShapeSignature, loadInitialShape]);

  return {
    controller,
    activeMode,
    hasCurrentFeature,
    clearCurrentFeature
  };
}
